import { PRICE_REPRESENTATION_MULTIPLIER } from '@/domain/Pricing';
import { hasSubscriptions, type Product } from '@/domain/Product';
import dispatchTrackingEvent, {
  dispatchNullEcommerceObject,
  productToGtmItemListPayload,
  productToGtmProductPayloadEvent,
  subscriptionToGtmSubscriptionPayloadEvent,
  type Ecommerce,
  type StructuredGtmPayload
} from '@/widgets/services/TrackingService.ts';

type GtmToggleFavorites = Omit<StructuredGtmPayload, 'action_type'> & {
  subscription_product_included: string;
  discovery_offer_included: string;
  click_location: string;
  item_list_name?: string;
  item_list_id?: string;
  item_id_event: string;
  item_name_event: string;
  item_category_event: string;
  item_range_event: string;
  item_technology_event: string;
  item_type_event: string;
  item_subscription_name_event?: string;
  item_subscription_category_event?: string;
} & { ecommerce: Ecommerce };

export const RAISED_BY = 'global_plp_v3';

export function trackProductIsFavoriteToggledFrom(
  discoveryOfferBundleProducts: string[],
  itemListName: string,
  hasBeenFavorited: boolean,
  currency: string,
  product: Product,
  trackingPosition: number,
  from: 'plp' | 'plp-alternatives' | 'pdp'
): void {
  const isDiscoveryOffer = discoveryOfferBundleProducts.includes(product.id);

  dispatchNullEcommerceObject();

  const subscriptionProductIncluded = hasSubscriptions(product);

  let price;

  if (product.type === 'capsule') {
    price =
      product.pricing?.pricePerCapsule !== undefined
        ? (product.pricing?.pricePerCapsule ?? 0) /
          PRICE_REPRESENTATION_MULTIPLIER
        : product.pricing.price / PRICE_REPRESENTATION_MULTIPLIER;
  } else {
    price = product.pricing.price / PRICE_REPRESENTATION_MULTIPLIER;
  }

  const payload: GtmToggleFavorites = {
    event_raised_by: RAISED_BY,
    item_list_id: RAISED_BY,
    item_list_name: itemListName,
    event: hasBeenFavorited ? 'add_to_wishlist' : 'remove_from_wishlist',
    click_location: 'plp',
    subscription_product_included: subscriptionProductIncluded
      ? 'true'
      : 'false',
    discovery_offer_included: isDiscoveryOffer.toString(),
    ...productToGtmProductPayloadEvent(product),
    item_subscription_name_event: product.subscriptionsInfo?.id ?? undefined,
    item_subscription_category_event:
      product.subscriptionsInfo?.type ?? undefined,
    ecommerce: {
      currency,
      value: price,
      items: [
        productToGtmItemListPayload(
          product,
          isDiscoveryOffer,
          trackingPosition,
          RAISED_BY,
          itemListName || RAISED_BY
        )
      ]
    }
  };

  const subscriptionData = subscriptionToGtmSubscriptionPayloadEvent(
    product.subscriptionsInfo
  );

  if (
    payload.ecommerce.items !== undefined &&
    payload.ecommerce.items[0] !== undefined
  ) {
    payload.ecommerce.items[0] = {
      ...payload.ecommerce.items[0],
      ...subscriptionData
    };
  }

  if (from === 'plp-alternatives') {
    payload.event_raised_by = 'global_plp_v3';
    payload.item_list_id = 'global_plp_v3';
    payload.item_list_name = `global_plp_v3_oos_alternative - ${product.name}`;
    payload.click_location = 'plp oos alternative';
  }

  if (from === 'pdp') {
    payload.event_raised_by = 'pdp_hero';
    payload.item_list_id = undefined;
    payload.item_list_name = undefined;
    payload.click_location = 'pdp hero';
  }

  // here we apply the comments on https://nestle.atlassian.net/browse/ITNP-161372?focusedCommentId=3517303 to avoid changing
  // the snapshots and existing tests. We just change the requested fields related to favorites
  payload.ecommerce.items = payload.ecommerce.items?.map((item) => ({
    ...item,
    item_added_by_user: undefined,
    price: (item as any).price,
    item_market_name: product?.nessoft?.internationalName,
    quantity: 1
  }));

  // not needed on the docs https://nestle.atlassian.net/wiki/spaces/DIGMAN/pages/434701073/add_to_wishlist
  delete (payload as any).item_price_event;

  dispatchTrackingEvent(payload);
}
